import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta, SITE_URL } from '@/components/PageMeta';
import ConcourHero from '@/components/concour/ConcourHero';
import ShareBloc from '@/components/concour/ShareBloc';
import { daysUntil, getOrgInitials } from '@/components/concour/helpers';
import { LeafletMap } from '@/components/map/mapLeaflet.tsx';
import { ROUTES } from '@/config/routes';
import {
  type Competition,
  getCompetitionApi,
  getCompetitionResultsApi,
  getCompetitionsApi,
} from '@/lib/api/competitionsApi';
import { getOrganizationApi } from '@/lib/api/organizationsApi';
import { createCheckoutSessionApi, getTeamsApi } from '@/lib/api/teamsApi';
import { getFileUrl } from '@/lib/file-url';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS_UPPER as TYPE_LABELS,
  formatDateFullFr,
} from '@shared';
import { CompetitionStatus, PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Clock,
  CreditCard,
  MapPin,
  Shield,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Concour() {
  const { id } = useParams<{ id: string }>();
  const compId = Number(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentResult = searchParams.get('payment') as 'success' | 'cancel' | null;
  const [retryLoading, setRetryLoading] = useState(false);
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const openCompetitionRegistration = useSaasStore(s => s.openCompetitionRegistration);

  const {
    data: competition,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.competitions.detail(compId),
    queryFn: () => getCompetitionApi(compId),
    enabled: !isNaN(compId),
  });

  const { data: org } = useQuery({
    queryKey: queryKeys.organizations.detail(competition?.organizationId ?? 0),
    queryFn: () => getOrganizationApi(competition!.organizationId),
    enabled: !!competition?.organizationId,
  });

  const { data: similarData } = useQuery({
    queryKey: queryKeys.competitions.list({
      type: competition?.type ?? undefined,
      status: CompetitionStatus.REGISTRATION_OPEN,
      limit: 4,
    }),
    queryFn: () =>
      getCompetitionsApi({
        type: competition!.type ?? undefined,
        status: CompetitionStatus.REGISTRATION_OPEN,
        limit: 4,
      }),
    enabled: !!competition?.type,
  });
  const similar = (similarData?.data ?? []).filter(c => c.id !== compId).slice(0, 3);

  // Check si l'utilisateur connecté est déjà inscrit à ce concours
  const { data: myTeamData } = useQuery({
    queryKey: queryKeys.teams.myTeam(compId, user?.id ?? 0),
    queryFn: () => getTeamsApi({ competitionId: compId, captainId: user!.id, limit: 10 }),
    enabled: isAuthenticated && !!user && !isNaN(compId),
  });
  const myTeam =
    myTeamData?.data?.find(t => t.registrationStatus !== 'CANCELLED' && t.registrationStatus !== 'REJECTED') ?? null;

  const { data: results } = useQuery({
    queryKey: ['competitions', compId, 'results'] as const,
    queryFn: () => getCompetitionResultsApi(compId),
    enabled: !!competition && !isError && competition.status === CompetitionStatus.FINISHED,
  });
  const hasResults = (results?.length ?? 0) > 0;
  const resultsUrl = ROUTES.CONCOURS_RESULTATS.replace(':id', String(compId));

  if (isLoading) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen'>
        <div className='max-w-7xl mx-auto px-6 py-12 space-y-6'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className='h-32 animate-pulse rounded-3xl bg-slate-200' />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !competition) {
    return (
      <div className='bg-[#F8FAFC] pt-32 min-h-screen flex items-center justify-center'>
        {/* URL dynamique non publiable (404 API, concours DRAFT/disabled…) : noindex. */}
        <PageMeta
          title='Concours introuvable'
          description="Ce concours n'existe pas ou n'est pas accessible publiquement."
          robots='noindex, nofollow'
        />
        <div className='max-w-lg mx-auto px-6'>
          <div className='bg-white border border-slate-200 rounded-3xl shadow-sm p-8 text-center'>
            <div className='w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <Shield className='w-8 h-8 text-amber-600' />
            </div>
            <h1 className='text-2xl font-bold text-slate-900 mb-2'>Concours introuvable</h1>
            <p className='text-slate-500 mb-6'>
              Ce concours n&apos;existe pas ou n&apos;est pas accessible publiquement.
            </p>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors'
            >
              Voir les concours disponibles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isFull = competition.maxTeams !== null && competition.currentTeams >= competition.maxTeams;
  const registrationExpired = !!competition.registrationEnd && new Date(competition.registrationEnd) < new Date();
  // Feature 7 (2026-08) — date d'ouverture des inscriptions : même publié, un
  // concours n'accepte pas d'inscription avant cette date (bloqué côté front,
  // et rejeté côté API — cf. teams.service).
  const registrationNotYetOpen =
    !!competition.registrationOpensAt && new Date(competition.registrationOpensAt) > new Date();
  const isOpen =
    competition.status === CompetitionStatus.REGISTRATION_OPEN &&
    !isFull &&
    !registrationExpired &&
    !registrationNotYetOpen &&
    openCompetitionRegistration;
  const isPast =
    competition.status === CompetitionStatus.FINISHED ||
    competition.status === CompetitionStatus.IN_PROGRESS ||
    (!!competition.date && new Date(competition.date) < new Date());

  const handleRetryPayment = async () => {
    if (!myTeam) return;
    setRetryLoading(true);
    try {
      const origin = window.location.origin;
      const concourPath = ROUTES.CONCOUR.replace(':id', String(compId));
      const { checkoutUrl } = await createCheckoutSessionApi(
        myTeam.id,
        `${origin}${concourPath}?payment=success`,
        `${origin}${concourPath}?payment=cancel`,
      );
      if (checkoutUrl) window.location.href = checkoutUrl;
    } catch {
      toast.error('Impossible de relancer le paiement. Contactez le support.');
      setRetryLoading(false);
    }
  };

  const dismissPaymentBanner = () => {
    navigate(ROUTES.CONCOUR.replace(':id', String(compId)), { replace: true });
  };

  const isPaidCompetition = (competition.price ?? 0) > 0;
  const isTeamConfirmed = myTeam?.registrationStatus === TeamRegistrationStatus.CONFIRMED;
  const isTeamPending = myTeam?.registrationStatus === TeamRegistrationStatus.PENDING;
  const isTeamPaymentFailed = !!myTeam && myTeam.paymentStatus === PaymentStatus.FAILED && isPaidCompetition;

  const shouldShowTeamSummaryBanner = !!myTeam && !isTeamPaymentFailed;
  const shouldShowPaymentSuccessConfirmed = paymentResult === 'success' && isTeamConfirmed;
  const shouldShowPaymentSuccessPending = paymentResult === 'success' && !!myTeam && isTeamPending;
  const shouldShowPaymentCancelled = paymentResult === 'cancel' && !!myTeam;
  const shouldShowPaymentFailed = isTeamPaymentFailed;

  const teamSummaryBannerConfig = isTeamConfirmed
    ? {
        container: 'bg-emerald-50 border-emerald-200',
        iconBg: 'bg-emerald-100',
        icon: 'text-emerald-600',
        title: isPast ? 'Vous avez participé à ce concours' : 'Vous êtes inscrit à ce concours',
        titleColor: 'text-emerald-900',
        text: 'text-emerald-700',
        action: 'bg-emerald-600 hover:bg-emerald-700',
      }
    : {
        container: 'bg-amber-50 border-amber-200',
        iconBg: 'bg-amber-100',
        icon: 'text-amber-600',
        title: isPast ? 'Inscription non confirmée' : 'Inscription en attente',
        titleColor: 'text-amber-900',
        text: 'text-amber-700',
        action: 'bg-amber-600 hover:bg-amber-700',
      };

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title={competition.name}
        description={`${competition.type ? TYPE_LABELS[competition.type] : '—'} — ${competition.level ? LEVEL_LABELS[competition.level] : '—'} — ${competition.date ? formatDateFullFr(competition.date) : '—'} — ${competition.venue ?? competition.city ?? ''}. ${competition.currentTeams}/${competition.maxTeams ?? '?'} équipes inscrites.`}
        image={competition.files?.[0] ? getFileUrl(competition.files[0]) : undefined}
        ogType='article'
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: competition.name,
          description: competition.description ?? undefined,
          startDate: competition.date,
          location: {
            '@type': 'Place',
            name: competition.venue ?? competition.city ?? 'France',
            address: {
              '@type': 'PostalAddress',
              addressLocality: competition.city ?? undefined,
              addressCountry: 'FR',
            },
          },
          organizer: {
            '@type': 'Organization',
            // Organisateur réel de l'événement (vendeur / merchant en Stripe Direct Charges).
            // Fallback : BoulesConnect si l'organisation n'est pas encore chargée.
            name: org?.name ?? 'BoulesConnect',
            // URL de la fiche publique de l'organisateur (fallback : plateforme).
            url: org ? `${SITE_URL}/organisateurs/${org.id}` : SITE_URL,
          },
          // Plateforme / éditeur
          publisher: {
            '@type': 'Organization',
            name: 'BoulesConnect',
            url: SITE_URL,
          },
          offers: {
            '@type': 'Offer',
            price: competition.price ? String(competition.price / 100) : '0',
            priceCurrency: 'EUR',
            availability:
              competition.status === 'REGISTRATION_OPEN' ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
            url: `${SITE_URL}/concours/${competition.id}`,
          },
          image: competition.files?.[0] ? getFileUrl(competition.files[0]) : undefined,
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Concours', item: `${SITE_URL}/concours` },
            {
              '@type': 'ListItem',
              position: 3,
              name: competition.name,
              item: `${SITE_URL}/concours/${competition.id}`,
            },
          ],
        }}
      />
      <ConcourHero
        competition={competition}
        myTeam={myTeam}
        isFull={isFull}
        isOpen={isOpen}
        hasResults={hasResults}
        resultsUrl={resultsUrl}
        registrationExpired={registrationExpired}
        registrationNotYetOpen={registrationNotYetOpen}
        registrationOpensAt={competition.registrationOpensAt}
        registrationDisabledByPlatform={!openCompetitionRegistration}
        isPast={isPast}
      />

      {/* Stats Bar */}
      <section className='px-6 -mt-8 relative'>
        <div className='max-w-5xl mx-auto'>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            {[
              {
                label: 'Places',
                value: `${competition.currentTeams}/${competition.maxTeams ?? '?'}`,
                icon: Users,
                color: 'bg-blue-50 text-blue-600',
              },
              {
                label: 'Joueurs/équipe',
                value: String(competition.playersPerTeam),
                icon: Shield,
                color: 'bg-emerald-50 text-emerald-600',
              },
              {
                label: 'Niveau',
                value: competition.level ? LEVEL_LABELS[competition.level] : '—',
                icon: Trophy,
                color: 'bg-purple-50 text-purple-600',
              },
              {
                label: isPast ? 'Statut' : 'Dans',
                value: isPast ? 'Terminé' : competition.date ? daysUntil(competition.date) : '—',
                icon: Clock,
                color: isPast ? 'bg-slate-50 text-slate-600' : 'bg-orange-50 text-orange-600',
              },
            ].map(stat => (
              <div key={stat.label} className='bg-white rounded-2xl border border-slate-200 shadow-lg p-5 text-center'>
                <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                  <stat.icon className='w-5 h-5' />
                </div>
                <div className='text-xl font-extrabold text-slate-900'>{stat.value}</div>
                <div className='text-sm text-slate-500'>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bannière inscription — neutre tant que l'inscription n'est pas confirmée, masquée si paiement échoué */}
      {myTeam && shouldShowTeamSummaryBanner && (
        <section className='px-6 pt-8'>
          <div className='max-w-5xl mx-auto'>
            <div
              className={`${teamSummaryBannerConfig.container} border rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center gap-4`}
            >
              <div
                className={`w-12 h-12 ${teamSummaryBannerConfig.iconBg} rounded-2xl flex items-center justify-center shrink-0`}
              >
                {isTeamConfirmed ? (
                  <CheckCircle2 className={`w-6 h-6 ${teamSummaryBannerConfig.icon}`} />
                ) : (
                  <Clock className={`w-6 h-6 ${teamSummaryBannerConfig.icon}`} />
                )}
              </div>
              <div className='flex-1'>
                <h3 className={`font-bold ${teamSummaryBannerConfig.titleColor} text-lg`}>
                  {teamSummaryBannerConfig.title}
                </h3>
                <p className={`${teamSummaryBannerConfig.text} text-sm mt-0.5`}>
                  Équipe <span className='font-semibold'>&quot;{myTeam.name}&quot;</span>
                  {myTeam.registrationStatus === TeamRegistrationStatus.CONFIRMED && (
                    <span className='ml-2 inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full'>
                      <BadgeCheck className='w-3 h-3' /> Confirmée
                    </span>
                  )}
                  {myTeam.registrationStatus === TeamRegistrationStatus.PENDING && (
                    <span className='ml-2 inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full'>
                      <Clock className='w-3 h-3' /> En attente
                    </span>
                  )}
                </p>
              </div>
              <Link
                to={ROUTES.ROLE_LANDING}
                className={`shrink-0 flex items-center gap-2 ${teamSummaryBannerConfig.action} text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-colors`}
              >
                Mon espace <ArrowRight className='w-4 h-4' />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Bannière succès paiement — uniquement si l'équipe est réellement CONFIRMED */}
      {shouldShowPaymentSuccessConfirmed && (
        <section className='px-6 pt-8'>
          <div className='max-w-5xl mx-auto'>
            <div className='bg-emerald-50 border border-emerald-200 rounded-3xl p-6 flex flex-col sm:flex-row sm:items-start gap-4'>
              <div className='w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center shrink-0'>
                <CheckCircle2 className='w-6 h-6 text-emerald-600' />
              </div>
              <div className='flex-1'>
                <h3 className='font-bold text-emerald-900 text-lg'>Inscription confirmée !</h3>
                <p className='text-emerald-700 text-sm mt-1'>
                  Votre équipe est bien inscrite à ce concours. Vous recevrez une confirmation par notification.
                </p>
              </div>
              <button
                onClick={dismissPaymentBanner}
                className='shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-emerald-600 hover:bg-emerald-100 transition-colors'
                aria-label='Fermer'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Bannière paiement validé mais confirmation en attente (webhook pas encore passé) */}
      {shouldShowPaymentSuccessPending && (
        <section className='px-6 pt-8'>
          <div className='max-w-5xl mx-auto'>
            <div className='bg-blue-50 border border-blue-200 rounded-3xl p-6 flex flex-col sm:flex-row sm:items-start gap-4'>
              <div className='w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center shrink-0'>
                <Clock className='w-6 h-6 text-blue-600' />
              </div>
              <div className='flex-1'>
                <h3 className='font-bold text-blue-900 text-lg'>Paiement reçu — confirmation en cours</h3>
                <p className='text-blue-700 text-sm mt-1'>
                  Votre paiement a été validé. La confirmation de l&apos;inscription peut prendre quelques instants.
                  Actualisez la page si nécessaire.
                </p>
              </div>
              <button
                onClick={dismissPaymentBanner}
                className='shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-blue-600 hover:bg-blue-100 transition-colors'
                aria-label='Fermer'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Bannière paiement annulé — uniquement si une équipe liée existe réellement */}
      {shouldShowPaymentCancelled && (
        <section className='px-6 pt-8'>
          <div className='max-w-5xl mx-auto'>
            <div className='bg-amber-50 border border-amber-200 rounded-3xl p-6 flex flex-col sm:flex-row sm:items-start gap-4'>
              <div className='w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center shrink-0'>
                <AlertTriangle className='w-6 h-6 text-amber-600' />
              </div>
              <div className='flex-1'>
                <h3 className='font-bold text-amber-900 text-lg'>Paiement non finalisé</h3>
                <p className='text-amber-700 text-sm mt-1'>
                  Votre équipe a bien été créée mais le paiement n&apos;a pas abouti. Vous pouvez réessayer maintenant —
                  votre place est réservée.
                </p>
                {myTeam && (competition.price ?? 0) > 0 && (
                  <button
                    onClick={() => void handleRetryPayment()}
                    disabled={retryLoading}
                    className='mt-4 inline-flex items-center gap-2 bg-amber-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-amber-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    <CreditCard className='w-4 h-4' />
                    {retryLoading ? 'Redirection…' : 'Réessayer le paiement'}
                  </button>
                )}
              </div>
              <button
                onClick={dismissPaymentBanner}
                className='shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-amber-600 hover:bg-amber-100 transition-colors'
                aria-label='Fermer'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Bannière paiement échoué */}
      {shouldShowPaymentFailed && (
        <section className='px-6 pt-8'>
          <div className='max-w-5xl mx-auto'>
            <div className='bg-red-50 border border-red-200 rounded-3xl p-6 flex flex-col sm:flex-row sm:items-start gap-4'>
              <div className='w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center shrink-0'>
                <AlertTriangle className='w-6 h-6 text-red-600' />
              </div>
              <div className='flex-1'>
                <h3 className='font-bold text-red-900 text-lg'>Paiement échoué</h3>
                <p className='text-red-700 text-sm mt-1'>
                  Votre paiement n&apos;a pas pu être validé. Vous pouvez réessayer — votre place est réservée.
                </p>
                {myTeam && (competition.price ?? 0) > 0 && (
                  <button
                    onClick={() => void handleRetryPayment()}
                    disabled={retryLoading}
                    className='mt-4 inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    <CreditCard className='w-4 h-4' />
                    {retryLoading ? 'Redirection…' : 'Réessayer le paiement'}
                  </button>
                )}
              </div>
              <button
                onClick={dismissPaymentBanner}
                className='shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-red-600 hover:bg-red-100 transition-colors'
                aria-label='Fermer'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Content */}
      <section className='px-6 py-12'>
        <div className='max-w-7xl mx-auto flex flex-col lg:flex-row gap-8'>
          {/* Left (2/3) */}
          <div className='lg:w-2/3 space-y-8'>
            {/* À propos */}
            <div className='bg-white rounded-3xl border border-slate-200 p-8'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-4'>À propos du concours</h2>
              {competition.description ? (
                <p className='text-slate-600 leading-relaxed'>{competition.description}</p>
              ) : (
                <p className='text-slate-400 italic'>Aucune description disponible pour ce concours.</p>
              )}
              {competition.registrationEnd && (
                <div className='mt-4 p-4 bg-amber-50 rounded-2xl border border-amber-200'>
                  <p className='text-sm text-amber-700 font-semibold'>
                    Date limite d&apos;inscription :{' '}
                    {new Date(competition.registrationEnd).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Informations pratiques */}
            <div className='bg-white rounded-3xl border border-slate-200 p-8'>
              <h2 className='text-2xl font-extrabold text-slate-900 mb-6'>Informations pratiques</h2>
              <div className='grid md:grid-cols-2 gap-6'>
                <div>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>Type</p>
                  <p className='text-slate-900 font-semibold'>
                    {competition.type ? TYPE_LABELS[competition.type] : '—'}
                  </p>
                </div>
                <div>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>Niveau</p>
                  <p className='text-slate-900 font-semibold'>
                    {competition.level ? LEVEL_LABELS[competition.level] : '—'}
                  </p>
                </div>
                <div>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>Joueurs / équipe</p>
                  <p className='text-slate-900 font-semibold'>{competition.playersPerTeam}</p>
                </div>
                {competition.minTeams && (
                  <div>
                    <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>
                      Équipes minimum
                    </p>
                    <p className='text-slate-900 font-semibold'>{competition.minTeams}</p>
                  </div>
                )}
                <div>
                  <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>
                    Réservé aux membres
                  </p>
                  <p className='text-slate-900 font-semibold'>{competition.restrictedToMembers ? 'Oui' : 'Non'}</p>
                </div>
                {competition.restrictedAffiliates && (
                  <div>
                    <p className='text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1'>
                      Clubs affiliés uniquement
                    </p>
                    <p className='text-slate-900 font-semibold'>Oui</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar (1/3) */}
          <div className='lg:w-1/3 space-y-6'>
            <div className='sticky top-28 space-y-6'>
              {/* Votre équipe */}
              {myTeam && (
                <div className='bg-emerald-50 border border-emerald-200 rounded-3xl p-6'>
                  <h3 className='font-bold text-lg text-emerald-900 mb-4 flex items-center gap-2'>
                    <CheckCircle2 className='w-5 h-5 text-emerald-600' /> Votre équipe
                  </h3>
                  <p className='font-bold text-slate-900 text-base mb-1'>{myTeam.name}</p>
                  {myTeam.players && myTeam.players.length > 0 && (
                    <ul className='mt-3 space-y-1.5'>
                      {myTeam.players.map((p, i) => (
                        <li key={p.id} className='flex items-center gap-2 text-sm text-slate-600'>
                          <span className='w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold shrink-0'>
                            {i + 1}
                          </span>
                          {p.firstName} {p.lastName}
                          {p.licenseNumber && <span className='text-xs text-slate-400 ml-auto'>{p.licenseNumber}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link
                    to={ROUTES.ROLE_LANDING}
                    className='mt-4 w-full flex items-center justify-center gap-2 bg-emerald-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-emerald-700 transition-colors'
                  >
                    Voir dans mon espace <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
              )}

              {/* Résultats */}
              {hasResults && (
                <div className='bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-3xl p-6'>
                  <h3 className='font-bold text-lg text-amber-900 mb-1 flex items-center gap-2'>
                    <Trophy className='w-5 h-5 text-amber-500' /> Résultats disponibles
                  </h3>
                  <p className='text-sm text-amber-700 mb-4'>Le classement final de ce concours a été publié.</p>
                  <Link
                    to={resultsUrl}
                    className='w-full flex items-center justify-center gap-2 bg-amber-500 text-white py-3 rounded-xl text-sm font-bold hover:bg-amber-600 transition-colors'
                  >
                    Voir le podium <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
              )}

              {/* Organisateur */}
              {org && (
                <div className='bg-white rounded-3xl border border-slate-200 p-6'>
                  <h3 className='font-bold text-lg text-slate-900 mb-4'>Organisateur</h3>
                  <div className='flex items-center gap-4 mb-4'>
                    <div className='w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg'>
                      {getOrgInitials(org.name)}
                    </div>
                    <div>
                      <h4 className='font-bold text-slate-900'>{org.name}</h4>
                      {org.city && <p className='text-sm text-slate-500'>{org.city}</p>}
                    </div>
                  </div>
                  <Link
                    to={ROUTES.ORGANIZATION_PROFILE.replace(':id', String(org.id))}
                    className='w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-700 py-3 rounded-xl text-sm font-semibold hover:bg-slate-200 transition-colors'
                  >
                    Voir le profil <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
              )}

              {/* Lieu */}
              {(competition.venue ?? competition.city) && (
                <div className='bg-white rounded-3xl border border-slate-200 p-6'>
                  <h3 className='font-bold text-lg text-slate-900 mb-4'>Lieu & Accès</h3>
                  <div className='flex items-start gap-3 text-sm text-slate-600 mb-4'>
                    <MapPin className='w-5 h-5 text-blue-500 shrink-0 mt-0.5' />
                    <div>
                      <p className='font-semibold text-slate-900'>{competition.venue}</p>
                      {competition.address && <p>{competition.address}</p>}
                      {!competition.address && competition.city && (
                        <p>
                          {competition.city}
                          {competition.department ? `, ${competition.department}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='aspect-video relative bg-gradient-to-br from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center'>
                    <LeafletMap lat={competition.latitude ?? undefined} lng={competition.longitude ?? undefined} />
                  </div>
                </div>
              )}

              <ShareBloc competition={competition as Competition} />

              {/* Concours similaires */}
              {similar.length > 0 && (
                <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-6 text-white'>
                  <h3 className='font-bold text-lg mb-4'>Concours similaires</h3>
                  <div className='space-y-3'>
                    {similar.map(c => (
                      <Link
                        key={c.id}
                        to={ROUTES.CONCOUR.replace(':id', String(c.id))}
                        className='block bg-white/10 rounded-xl p-4 hover:bg-white/20 transition-colors'
                      >
                        <h4 className='font-bold text-sm'>{c.name}</h4>
                        <div className='flex items-center justify-between text-xs text-blue-200 mt-1'>
                          <span>
                            {c.date
                              ? new Date(c.date).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                })
                              : '—'}
                          </span>
                          {c.price && <span className='font-bold text-white'>{(c.price / 100).toFixed(0)}€</span>}
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to={ROUTES.SEARCH_CONCOURS}
                    className='mt-4 w-full flex items-center justify-center gap-2 bg-white text-blue-700 py-3 rounded-xl font-bold text-sm hover:bg-blue-50 transition-colors'
                  >
                    Voir tous les concours <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
